export type DecimalParts = { digits: bigint; scale: bigint }

export interface PaymentProduct {
  id: string
  products_name: string
  unit_price: string
  points_per_unit: string
}

const maximumQuantity = 999_999_999_999n
const maximumPointUnits = 100_000_000_000_000_000_000n

export function decimalParts(value: string): DecimalParts | null {
  if (typeof value !== 'string' || value.length > 96) return null
  const match = /^(0|[1-9][0-9]{0,39})(?:\.([0-9]{1,40}))?(?:[eE]([+-]?[0-9]{1,2}))?$/.exec(value.trim())
  if (!match) return null
  const [, whole = '', fraction = '', exponent = '0'] = match
  let digits = `${whole}${fraction}`.replace(/^0+/, '')
  if (!digits) return null
  let scale = fraction.length - Number(exponent)
  const trim = Math.min(digits.length - digits.replace(/0+$/, '').length, Math.max(0, scale))
  if (trim) {
    digits = digits.slice(0, -trim)
    scale -= trim
  }
  if (scale > 18 || digits.length - scale > 20) return null
  if (scale < 0) {
    digits += '0'.repeat(-scale)
    scale = 0
  }
  return { digits: BigInt(digits), scale: 10n ** BigInt(scale) }
}

export function decimalText({ digits, scale }: DecimalParts): string {
  const fraction = (digits % scale).toString().padStart(scale.toString().length - 1, '0').replace(/0+$/, '')
  return `${digits / scale}${fraction ? `.${fraction}` : ''}`
}

export function yuanText(minor: bigint): string {
  return `${minor / 100n}.${(minor % 100n).toString().padStart(2, '0')}`
}

export function amountMinor(value: string): bigint | null {
  const normalized = value.trim()
  if (!/^(?:0|[1-9][0-9]*)(?:\.[0-9]{0,2})?$/.test(normalized) || normalized.length > 20) return null
  const [whole, fraction = ''] = normalized.split('.')
  const minor = BigInt(whole!) * 100n + BigInt((fraction + '00').slice(0, 2))
  return minor > 0n ? minor : null
}

export function amountError(value: string): string {
  const normalized = value.trim()
  if (!/^(?:0|[1-9][0-9]*)(?:\.[0-9]{0,2})?$/.test(normalized)) {
    return '请输入 0.01 元起的充值金额，最多两位小数。'
  }
  return amountMinor(value) === null ? '最低充值金额为 0.01 元。' : ''
}

export function quantityForAmount(value: string, product: PaymentProduct | undefined): string | null {
  const minor = amountMinor(value)
  const price = product ? decimalParts(product.unit_price) : null
  if (minor === null || price === null) return null
  const numerator = minor * price.scale
  const denominator = 100n * price.digits
  if (numerator % denominator !== 0n) return null
  const quantity = numerator / denominator
  return quantity > 0n && quantity <= maximumQuantity ? quantity.toString() : null
}

export function amountForQuantity(quantity: string, product: PaymentProduct | undefined): string | null {
  if (!/^[1-9][0-9]{0,11}$/.test(quantity)) return null
  const price = product ? decimalParts(product.unit_price) : null
  if (price === null) return null
  const numerator = price.digits * BigInt(quantity) * 100n
  if (numerator <= 0n || numerator % price.scale !== 0n) return null
  return yuanText(numerator / price.scale)
}

/** Returns exact received points scaled by 1e6; null means the product contract cannot fit this UI or DB. */
export function pointUnitsScaled(quantity: string, product: PaymentProduct | undefined): bigint | null {
  if (!/^[1-9][0-9]{0,11}$/.test(quantity)) return null
  const pointsPerUnit = product ? decimalParts(product.points_per_unit) : null
  if (pointsPerUnit === null) return null
  const numerator = pointsPerUnit.digits * BigInt(quantity) * 1_000_000n
  if (numerator % pointsPerUnit.scale !== 0n) return null
  const units = numerator / pointsPerUnit.scale
  return units > 0n && units <= maximumPointUnits ? units : null
}

export function pointText(unitsScaled: bigint): string {
  return decimalText({ digits: unitsScaled, scale: 1_000_000n })
}

export function unitPriceText(product: PaymentProduct | undefined): string {
  const price = product ? decimalParts(product.unit_price) : null
  return price ? decimalText(price) : '—'
}

